import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { auctionType as auctionTypeRef } from '../../types';
import Spinner from '../Spinner';
import Error from '../errors/Error';
import DateTime, { LONG_DATE } from '../DateTime';
import NumberField from '../fields/NumberField';
import CheckboxField from '../fields/CheckboxField';
import useSingleValidator from '../../hooks/useSingleValidator';
import useVolumeDistribution, { PRESELECTED_AUCTION_COUNT } from '../../hooks/useVolumeDistribution';
import {
    distributionAuctioneerAllocationError,
    distributionAuctioneerSumError,
    distributionAuctioneerVolumeValidator,
    distributionTotalError,
    distributionVolumeValidator,
} from '../../validators/volumeDistribution';
import Constants from '../../utils/Constants';
import { totalVolumeOf } from '../../utils/volumeDistribution';
import DistributeVolumeAuctioneerRows from './DistributeVolumeAuctioneerRows';
import './_styles_/DistributeVolumeModal.css';

export const DEFAULT_DISTRIBUTION_STEP = 500;

const BASE_COLUMN_COUNT = 6;

const noop = () => {};

DistributeVolumeModal.propTypes = {
    cancelledAuction: auctionTypeRef.isRequired,
    setVariables: PropTypes.func.isRequired,
    setValid: PropTypes.func.isRequired,
    setFullScreen: PropTypes.func,
    step: PropTypes.number,
};

function DistributeVolumeModal(props) {
    const {
        cancelledAuction,
        setVariables,
        setValid,
        setFullScreen = noop,
        step = DEFAULT_DISTRIBUTION_STEP,
    } = props;
    const { t } = useTranslation();
    const cancelledQuantity = cancelledAuction.quantity;
    const {
        loading,
        error,
        upcomingAuctions,
        distributableAuctions,
        hasAuctioneerBreakdown,
        isCompatible,
        linesOf,
        selected,
        volumes,
        auctioneerVolumes,
        expanded,
        initialised,
        indivisibleVolume,
        noAuctionsAvailable,
        noDistributableAuctions,
        fewerAuctionsThanExpected,
        hasSelection,
        selectedAuctions,
        parsedVolume,
        auctioneerTotalOf,
        allocationOf,
        cancelledVolumeOf,
        allocationLines,
        isValid,
        distributions,
        onToggle,
        onVolumeChange,
        onAuctioneerVolumeChange,
        onToggleExpanded,
    } = useVolumeDistribution(cancelledAuction, step);

    const { validator: volumeValidator } = useSingleValidator(true, useMemo(() => distributionVolumeValidator(t, step), [t, step]));
    const { validator: auctioneerVolumeValidator } = useSingleValidator(true, useMemo(() => distributionAuctioneerVolumeValidator(t, step), [t, step]));
    const totalErrorProvider = useMemo(() => distributionTotalError(t, cancelledQuantity), [t, cancelledQuantity]);
    const auctioneerSumErrorProvider = useMemo(() => distributionAuctioneerSumError(t), [t]);
    const allocationErrorProvider = useMemo(() => distributionAuctioneerAllocationError(t), [t]);
    const [totalError, setTotalError] = useState(null);
    const [auctioneerSumError, setAuctioneerSumError] = useState(null);
    const [allocationError, setAllocationError] = useState(null);
    const debouncedTotalError = useDebouncedCallback(
        (selection) => setTotalError(totalErrorProvider(selection)),
        Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS,
    );
    const debouncedAuctioneerSumError = useDebouncedCallback(
        (auctions) => setAuctioneerSumError(auctioneerSumErrorProvider(auctions)),
        Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS,
    );
    const debouncedAllocationError = useDebouncedCallback(
        (lines) => setAllocationError(allocationErrorProvider(lines)),
        Constants.DISPLAY_FIELD_ERROR_DELAY_IN_MS,
    );

    useEffect(() => {
        debouncedTotalError(selectedAuctions.map((auction) => ({
            auctionId: auction.id,
            volume: parsedVolume(auction.id),
        })));
        debouncedAuctioneerSumError(hasAuctioneerBreakdown ? selectedAuctions.map((auction) => ({
            name: auction.name,
            volume: parsedVolume(auction.id),
            auctioneerTotal: auctioneerTotalOf(auction),
        })) : []);
        debouncedAllocationError(hasAuctioneerBreakdown ? allocationLines : []);
        setValid(isValid);
        setVariables({
            cancelledAuctionId: cancelledAuction.id,
            distributions: isValid ? distributions : [],
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isValid, JSON.stringify(volumes), JSON.stringify(selected), JSON.stringify(auctioneerVolumes), upcomingAuctions.length]);

    // The table with its expandable break downs needs the whole screen; a lone message does not.
    // Waiting for the proposal keeps an indivisible volume from flashing up full screen first.
    const showsAuctions = !loading && !error && initialised && !indivisibleVolume && !noAuctionsAvailable;
    useEffect(() => {
        setFullScreen(showsAuctions);
    }, [showsAuctions, setFullScreen]);

    if (loading) {
        return <Spinner className="distribute-volume-modal--loading" />;
    }
    if (error) {
        return <Error className="distribute-volume-modal--error" error={error} />;
    }

    if (indivisibleVolume) {
        return (
            <div className="distribute-volume-modal">
                <div className="distribute-volume-modal--indivisible validation-error">
                    {t('auctions.distribute-volume.indivisible-volume', {
                        quantity: cancelledQuantity,
                        step,
                    })}
                </div>
            </div>
        );
    }

    const columnCount = BASE_COLUMN_COUNT + (hasAuctioneerBreakdown ? 1 : 0);

    return (
        <div className="distribute-volume-modal">
            <p className="distribute-volume-modal--summary">
                {t('auctions.distribute-volume.summary', {
                    quantity: cancelledQuantity,
                    step,
                })}
            </p>
            {noAuctionsAvailable && (
                <div className="distribute-volume-modal--not-enough validation-error">
                    {t('auctions.distribute-volume.not-enough-auctions')}
                </div>
            )}
            {noDistributableAuctions && (
                <div className="distribute-volume-modal--none-distributable validation-error">
                    {t('auctions.distribute-volume.no-distributable-auctions')}
                </div>
            )}
            {fewerAuctionsThanExpected && (
                <div className="distribute-volume-modal--fewer-auctions validation-warning">
                    {t('auctions.distribute-volume.fewer-auctions', {
                        available: distributableAuctions.length,
                        expected: PRESELECTED_AUCTION_COUNT,
                    })}
                </div>
            )}
            {!noAuctionsAvailable && (
                <div className="distribute-volume-modal--table-container">
                    <table className="distribute-volume-modal--table">
                        <thead>
                            <tr>
                                {hasAuctioneerBreakdown && (
                                    <th aria-label={t('auctions.distribute-volume.column.auctioneers')} />
                                )}
                                <th>{t('auctions.distribute-volume.column.selected')}</th>
                                <th>{t('auctions.distribute-volume.column.name')}</th>
                                <th>{t('auctions.distribute-volume.column.opening')}</th>
                                <th>{t('auctions.distribute-volume.column.original-volume')}</th>
                                <th>{t('auctions.distribute-volume.column.distributed-volume')}</th>
                                <th>{t('auctions.distribute-volume.column.total-volume')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {upcomingAuctions.map((auction) => {
                                const isSelected = !!selected[auction.id];
                                const isExpanded = !!expanded[auction.id];
                                const canReceiveVolume = isCompatible(auction);
                                return (
                                    <React.Fragment key={auction.id}>
                                        <tr className={canReceiveVolume
                                            ? 'distribute-volume-modal--row'
                                            : 'distribute-volume-modal--row distribute-volume-modal--row--incompatible'}
                                        >
                                            {hasAuctioneerBreakdown && (
                                                <td>
                                                    {canReceiveVolume && (
                                                        <button
                                                            type="button"
                                                            className="distribute-volume-modal--auctioneers-toggle"
                                                            onClick={() => onToggleExpanded(auction.id)}
                                                            title={t('auctions.distribute-volume.toggle-auctioneers')}
                                                            aria-label={t('auctions.distribute-volume.toggle-auctioneers')}
                                                            aria-expanded={isExpanded}
                                                        />
                                                    )}
                                                </td>
                                            )}
                                            <td>
                                                <CheckboxField
                                                    className="distribute-volume-modal--select"
                                                    inputId={`distribute-volume-modal--select-${auction.id}`}
                                                    value={isSelected}
                                                    onChange={(isChecked) => onToggle(auction, isChecked)}
                                                    isDisabled={!canReceiveVolume}
                                                />
                                            </td>
                                            <td className="distribute-volume-modal--name">
                                                {auction.name}
                                                {!canReceiveVolume && (
                                                    <div className="distribute-volume-modal--incompatible validation-warning">
                                                        {t('auctions.distribute-volume.incompatible-auctioneers')}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="distribute-volume-modal--opening">
                                                <DateTime value={auction.openingTime} format={LONG_DATE} />
                                            </td>
                                            <td className="distribute-volume-modal--original-volume">{auction.quantity}</td>
                                            <td className="distribute-volume-modal--volume">
                                                <NumberField
                                                    className="distribute-volume-modal--volume-input"
                                                    inputId={`distribute-volume-modal--volume-input-${auction.id}`}
                                                    value={volumes[auction.id] ?? ''}
                                                    onChange={(newValue) => onVolumeChange(auction.id, newValue)}
                                                    validator={volumeValidator}
                                                    step={step}
                                                    min={0}
                                                    fractionDigits={0}
                                                    noThousandSeparator
                                                    roundToStepMultiple
                                                    disabled={!isSelected || !canReceiveVolume}
                                                />
                                            </td>
                                            <td className="distribute-volume-modal--total-volume">
                                                {totalVolumeOf(auction.quantity, volumes[auction.id])}
                                            </td>
                                        </tr>
                                        {hasAuctioneerBreakdown && isExpanded && canReceiveVolume && (
                                            <DistributeVolumeAuctioneerRows
                                                auctionId={auction.id}
                                                items={linesOf(auction)}
                                                volumes={auctioneerVolumes}
                                                onVolumeChange={onAuctioneerVolumeChange}
                                                validator={auctioneerVolumeValidator}
                                                step={step}
                                                columnCount={columnCount}
                                                allocationOf={allocationOf}
                                                cancelledVolumeOf={cancelledVolumeOf}
                                                disabled={!isSelected}
                                            />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            {!noAuctionsAvailable && !hasSelection && (
                <div className="distribute-volume-modal--no-selection validation-error">
                    {t('auctions.distribute-volume.validation.no-selection')}
                </div>
            )}
            {!noAuctionsAvailable && auctioneerSumError && (
                <div className="distribute-volume-modal--auctioneer-sum-error validation-error">
                    {auctioneerSumError}
                </div>
            )}
            {!noAuctionsAvailable && allocationError && (
                <div className="distribute-volume-modal--allocation-error validation-error">
                    {allocationError}
                </div>
            )}
            {!noAuctionsAvailable && totalError && (
                <div className="distribute-volume-modal--total-error validation-error">
                    {totalError}
                </div>
            )}
        </div>
    );
}

export default DistributeVolumeModal;
